import {
  Box,
  Collapse,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useDashboardFilters from '../../../hooks/useDashboardFilters'
import useGames, { type GamesList } from '../../../hooks/useGames/useGames'
import SearchIcon from '../../../icons/Search'
import { useTheme } from '../../../theme'
import getGameStatusLabelColor from '../../../utils/getGameStatusLabelColor'
import GameAvatar from '../../GameAvatar'

interface AddGameSearchProps {
  isOpen: boolean
  onClose: () => void
}

const AddGameSearch = ({ isOpen, onClose }: AddGameSearchProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const theme = useTheme()
  const allGames = useGames()
  const { isFavourite, addFavourite } = useDashboardFilters()

  const availableGames = useMemo(() => {
    if (!allGames) return []

    // Filter out already favorited games
    const nonFavoriteGames = allGames.filter((game) => !isFavourite(game.id))

    // Filter by search query if provided
    if (!searchQuery.trim()) return nonFavoriteGames

    return nonFavoriteGames.filter((game) =>
      game.info.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [allGames, isFavourite, searchQuery])

  const handleAddGame = (game: GamesList[0]) => {
    addFavourite(game.id)
    setSearchQuery('')
    onClose()
  }

  // Focus the search input when the component opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure the collapse animation has started
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Collapse in={isOpen}>
        <TextField
          fullWidth
          size='small'
          placeholder={t('games.searchGames')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
            sx: {
              height: 40,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: theme.palette.primary.contrastText,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.contrastText,
              },
            },
          }}
          sx={{
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 1,
            },
          }}
        />
      </Collapse>

      {/* Search Results Dropdown */}
      {isOpen && searchQuery.trim() && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: theme.colors.header,
            border: `1px solid ${theme.colors.headerDividers}`,
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto',
            zIndex: 1000,
          }}
        >
          <List sx={{ py: 0 }}>
            {availableGames.slice(0, 5).map((game) => (
              <ListItemButton
                key={game.id}
                onClick={() => handleAddGame(game)}
                sx={{
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: theme.colors.darkBlue,
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 43 }}>
                  <GameAvatar
                    gameId={game.id}
                    sx={{
                      width: 32,
                      height: 32,
                      border: `3px solid ${getGameStatusLabelColor(game.info.status)}`,
                    }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={game.info.name}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: theme.palette.primary.contrastText,
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </ListItemButton>
            ))}
            {availableGames.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <ListItemText
                  primary={t('common.noGamesFound')}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            )}
          </List>
        </Box>
      )}
    </Box>
  )
}

export default AddGameSearch
